import React, { useState, useContext } from "react";
import { Button, Tooltip, OverlayTrigger } from "react-bootstrap";
import { IconUserPlus, IconAlertCircle } from "@tabler/icons-react";
import CreateSubaccountModal from "../modals/CreateSubAccountModal";
import UserContext from "../../../contexts/UserContext";

const CreateSubaccountButton = ({
  onSubaccountCreated,
  variant = "primary",
  size = "md", 
  className = "",
  disabled = false,
  children,
}) => {
  const { 
    isOrganization, 
    canCreateSubaccounts, 
    user, 
    getOrganizationInfo 
  } = useContext(UserContext);
  
  const [showModal, setShowModal] = useState(false);

  // Obter dados da organização do contexto
  const organization = getOrganizationInfo();

  // Verificar permissões usando novo UserContext
  const hasPermissionToCreate = () => {
    // Deve ser organização
    if (!isOrganization()) return false;
    
    // Deve poder criar subcontas (baseado no account_context)
    if (!canCreateSubaccounts()) return false;
    
    // Deve ser dono da organização (superuser level 1)
    if (!user?.is_superuser || user?.superuser_level !== 1) return false;
    
    return true;
  };

  const canCreateSubaccount = hasPermissionToCreate();
  const isDisabled = disabled || !canCreateSubaccount;

  const handleOpenModal = () => {
    if (!isDisabled) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSuccess = (result) => {
    setShowModal(false);
    if (onSubaccountCreated) {
      onSubaccountCreated(result);
    }
  };

  // Tooltip para quando não pode criar subcontas
  const renderTooltip = (props) => (
    <Tooltip id="create-subaccount-tooltip" {...props}>
      {!isOrganization()
        ? "Disponível apenas para organizações"
        : !canCreateSubaccounts()
        ? "Sua organização não pode criar subcontas"
        : !user?.is_superuser || user?.superuser_level !== 1
        ? "Apenas donos de organização podem criar subcontas"
        : !organization
        ? "Dados da organização não disponíveis"
        : "Criar nova subconta"}
    </Tooltip>
  );

  const buttonContent = children || (
    <>
      <IconUserPlus size={16} />
      Criar Subconta
    </>
  );

  const button = (
    <Button
      variant={isDisabled ? "outline-secondary" : variant}
      size={size}
      onClick={handleOpenModal}
      disabled={isDisabled}
      className={`d-flex align-items-center gap-2 ${className}`}
    >
      {isDisabled && <IconAlertCircle size={16} />}
      {buttonContent}
    </Button>
  );

  return (
    <>
      {/* Render button with tooltip if disabled, otherwise just button */}
      {isDisabled ? (
        <OverlayTrigger
          placement="top"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip}
        >
          <span className="d-inline-block">{button}</span>
        </OverlayTrigger>
      ) : (
        button
      )}

      {/* Modal */}
      <CreateSubaccountModal
        show={showModal}
        onHide={handleCloseModal}
        organization={organization}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default CreateSubaccountButton;
